import {
  ColDef,
  FilterChangedEvent,
  GetRowIdFunc,
  ICellRendererParams,
  IServerSideDatasource,
  ISetFilter,
  ITextFilterParams,
  ModuleRegistry,
  SelectionChangedEvent,
  SetFilterModel,
  ValueFormatterParams
} from "@ag-grid-community/core";
import {AgGridReact} from "@ag-grid-community/react";
import {ColumnsToolPanelModule} from "@ag-grid-enterprise/column-tool-panel";
import {ServerSideRowModelModule} from "@ag-grid-enterprise/server-side-row-model";
import {SetFilterModule} from "@ag-grid-enterprise/set-filter";
import {useApolloClient} from "@apollo/client";
import {ControlGroup, Intent, Text} from "@blueprintjs/core";
import {AppToasterTopLeft} from "../../utils/toasters/toaster";
import log from "loglevel";

import {isArray, isEqual} from "lodash";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Driver, ModelDriverFilterInput, useSearchVehicleTypesQuery} from "../../generated/graphql";
import {extractJsonPref, usePreferenceContext} from "../../providers/PreferenceProvider";

import {useAuthState} from "../../providers/AuthProvider";
import {EmptyValueStrings, UserPreferences} from "../../constants/constants";
import {useDispatchGroupContext} from "../../providers/DispatchGroupProvider";
import MultiSelectDropDown, {MultiSelectDropDownItem} from "../common/MultiSelectDropDown";
import Search from "../common/Search";
import "./styles/grids.css";

import {CustomRowStyle} from "../job/JobPanel";
import {
  RoutePlanningActionType,
  RoutePlanningDriver,
  useRoutePlanningContext,
  useRoutePlanningDispatch
} from "../../providers/RoutePlanningProvider";
import {AddressType} from "./types/types";
import {getDriverAddressText} from "../../utils/General";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import VehicleIconResolver from "../vehicles/VehicleIconResolver";
import {
  ButtonBar,
  ButtonContainer,
  DangerButton,
  LeftControls,
  PanelContainer,
  RightControls,
  SelectedInfo
} from "./styles/styleds";
import {hasSelectedDispatchStations, useDispatchStationContext} from "../../providers/DispatchStationProvider";
import {createDriverServerSideDatasource} from "../../services/datasources/DriverServerSideDataSource.service";
import {useFeatureFlagContext} from "../../providers/FeatureFlagProvider";
import {useDispatchDataContext} from "../../providers/DispatchDataProvider";

ModuleRegistry.registerModules([ServerSideRowModelModule, SetFilterModule, ColumnsToolPanelModule]);

type GridContext = {
  globalQuery?: string;
  globalFilter?: ModelDriverFilterInput;
};

const getRowId: GetRowIdFunc<Driver> = (params) => params.data.driverId!.toString();

export const ColumnTypes: {[key: string]: ColDef<Driver>} = {
  filterColumn: {
    suppressMenu: false,
    menuTabs: ["filterMenuTab"],
    filterParams: {
      buttons: ["apply", "reset"],
      closeOnApply: true
    }
  },
  textFilter: {
    filter: "agTextColumnFilter",
    filterParams: {
      filterOptions: ["contains", "notContains", "equals", "notEqual", "blank", "notBlank"]
    } as ITextFilterParams
  },
  numberFilter: {
    filter: "agNumberColumnFilter",
    filterParams: {
      filterOptions: [
        "equals",
        "notEqual",
        "lessThan",
        "lessThanOrEqual",
        "greaterThan",
        "greaterThanOrEqual",
        "inRange"
      ]
    }
  }
};

const DefaultColumnDef: ColDef = {
  resizable: true,
  sortable: true,
  minWidth: 40,
  suppressMenu: true,
  flex: 1
};

const DriversGrid = () => {
  const {multipleSort: multipleSortEnabled, itineraryPlanningSkills: skillsEnabled} = useFeatureFlagContext();
  const {skills: allSkills} = useDispatchDataContext();
  const apolloClient = useApolloClient();
  const authState = useAuthState();
  const {userPreferences, tenantPreferences, isUserPreferencesLoaded} = usePreferenceContext();
  const dispatchStationState = useDispatchStationContext();
  const {selectedDispatchStations} = dispatchStationState;
  const {driverGroups} = useDispatchGroupContext();
  const dispatch = useRoutePlanningDispatch();
  const {selectedDrivers} = useRoutePlanningContext();
  const newDrivers = useMemo(() => selectedDrivers.filter((d) => !d.manifestDriverId), [selectedDrivers]);
  const gridRef = useRef<AgGridReact<Driver>>(null);

  const [initializing, setInitializing] = useState<boolean>(true);
  const [gridReady, setGridReady] = useState<boolean>(false);
  const [gridHasActiveFilters, setGridHasActiveFilters] = useState<boolean>(false);
  const [datasource, setDatasource] = useState<IServerSideDatasource>();

  const [selectedDriverGroupIds, setSelectedDriverGroupIds] = useState<number[]>([]);
  const [gridContext, setGridContext] = useState<GridContext>({
    globalQuery: ""
  });

  const vehicleTypesResults = useSearchVehicleTypesQuery({
    variables: {
      limit: 1000
    }
  });

  const driverGroupOptions = useMemo(() => {
    const map: Map<number, MultiSelectDropDownItem> = new Map();
    const sortedGroups = [...driverGroups].sort((a, b) => (a.dispatchGroup ?? "").localeCompare(b.dispatchGroup ?? ""));
    for (const group of sortedGroups) {
      map.set(group.dispatchGroupId!, {
        name: group.dispatchGroup,
        selected: selectedDriverGroupIds.includes(group.dispatchGroupId!),
        id: group.dispatchGroupId
      } as MultiSelectDropDownItem);
    }
    return map;
  }, [driverGroups, selectedDriverGroupIds]);

  const refreshDataByFilterChanged = useCallback(
    (eventName: string) => {
      if (gridRef.current) {
        log.debug(`Refreshing data for event: ${eventName}`);
        gridRef.current.api.onFilterChanged();
      }
    },
    [gridRef]
  );

  const getFilterInstance = useCallback((colId: string) => {
    if (!gridRef.current?.api) {
      return Promise.resolve(null);
    }
    return new Promise<ISetFilter | null>((resolve) => {
      gridRef.current!.api.getFilterInstance(colId, (filter) => {
        resolve(filter as ISetFilter);
      });
    });
  }, []);

  const handleGridReady = useCallback(() => {
    setGridReady(true);
  }, []);

  const handleFilterChanged = useCallback((event: FilterChangedEvent<Driver>) => {
    setGridHasActiveFilters(Object.keys(event.api.getFilterModel()).length > 0);
    if (event.columns[0]?.getColId() === "dispatchGroup.dispatchGroupId") {
      const filterModel = event.api.getFilterModel();
      const driverGroupFilter = filterModel["dispatchGroup.dispatchGroupId"] as SetFilterModel | undefined;
      if (driverGroupFilter) {
        const selectedValues = driverGroupFilter.values.map(Number);
        if (selectedValues && selectedValues.length > 0) {
          setSelectedDriverGroupIds(selectedValues);
        } else {
          // If unselected all job groups, set -1 to ensure no results are shown
          setSelectedDriverGroupIds([-1]);
        }
      } else {
        setSelectedDriverGroupIds([]);
      }
    }
  }, []);

  const syncModelFilter = useCallback(
    (filterName: string, filterModel: any) => {
      getFilterInstance(filterName)
        .then((filter) => {
          if (!isEqual(filter?.getModel(), filterModel)) {
            filter?.setModel(filterModel);
          }
        })
        .finally(() => {
          refreshDataByFilterChanged(filterName);
        });
    },
    [getFilterInstance, refreshDataByFilterChanged]
  );

  const handleDriverGroupSelected = (key: number) => {
    setSelectedDriverGroupIds([...selectedDriverGroupIds, key]);
  };

  const handleDriverGroupUnselected = (key: number) => {
    const newSelectedGroups = selectedDriverGroupIds.filter((groupId) => groupId !== key);
    setSelectedDriverGroupIds(newSelectedGroups);
  };

  const handleDriverGroupUnselectedAll = useCallback(() => {
    setSelectedDriverGroupIds([]);
  }, []);

  const handleSelectionChanged = useCallback(
    (event: SelectionChangedEvent<Driver>) => {
      const vehicleTypes = vehicleTypesResults.data?.searchVehicleTypes?.items ?? [];
      const selectedNodes = event.api.getSelectedNodes();
      const existingDriverMap = new Map(selectedDrivers.map((d) => [d.driverId, d]));
      const manifestDriverIds = new Set(selectedDrivers.filter((d) => !!d.manifestDriverId).map((d) => d.driverId));

      const conflictDrivers: string[] = [];
      const newDrivers: RoutePlanningDriver[] = [];

      for (const node of selectedNodes) {
        const driver = node.data;
        if (!driver) continue;

        // Check if this driver already has a manifest selected
        if (manifestDriverIds.has(driver.driverId as number)) {
          const driverLabel =
            [driver.name, driver.driverCode].filter(Boolean).join(" - ") || `Driver ${driver.driverId}`;
          conflictDrivers.push(driverLabel);
          node.setSelected(false, false);
          continue;
        }

        const existing = existingDriverMap.get(driver.driverId);
        if (existing) {
          newDrivers.push(existing);
        } else {
          newDrivers.push({
            ...driver,
            startLocation: getDriverAddressText(driver),
            startLocationType: AddressType.HOME,
            endLocation: null,
            endLocationType: null,
            startDate: null,
            startTime: null,
            startTimeZone: null,
            vehicleDescription:
              vehicleTypes.find((item) => item?.vehicleTypeId === driver.vehicleTypeId)?.description ?? null,
            isStartDateTimeValid: false,
            isStartLocationValid: false,
            isEndLocationValid: true
          } as RoutePlanningDriver);
        }
      }

      if (conflictDrivers.length > 0) {
        AppToasterTopLeft.show({
          message: (
            <span>
              <i>{conflictDrivers.join(", ")}</i> {conflictDrivers.length > 1 ? "are" : "is"} already selected as an
              Existing Manifest. Remove them first to select as a new driver.
            </span>
          ),
          intent: Intent.WARNING
        });
      }

      dispatch({type: RoutePlanningActionType.SET_SELECTED_DRIVERS, payload: newDrivers});
    },
    [dispatch, vehicleTypesResults.data?.searchVehicleTypes?.items, selectedDrivers]
  );

  const handleClearAllSelectedDrivers = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.api.deselectAll();
    }
    dispatch({type: RoutePlanningActionType.CLEAR_ALL_DRIVERS});
  }, [dispatch]);

  const handleGlobalSearch = useCallback(
    (query: string) => {
      if (gridContext.globalQuery != query) {
        setGridContext({...gridContext, globalQuery: query});
      }
    },
    [gridContext]
  );

  const columnDefs: ColDef<Driver>[] = useMemo(() => {
    return [
      {
        field: "name",
        headerName: "Driver Name",
        type: ["filterColumn", "textFilter"],
        flex: 3
      },
      {
        field: "driverCode",
        headerName: "Driver Code",
        type: ["filterColumn", "textFilter"]
      },
      {
        field: "dispatchGroupId",
        headerName: "Group",
        sortable: false,
        type: ["filterColumn"],
        filter: "agSetColumnFilter",
        filterParams: {
          values: Array.from(driverGroupOptions.values()).map((item) => item.id.toString()),
          valueFormatter: (params: ValueFormatterParams<number>) => {
            const groupName = driverGroupOptions.get(Number(params.value))?.name;
            return groupName ?? EmptyValueStrings.notProvided;
          },
          comparator: (groupAId: number, groupBId: number) => {
            const groupAName = driverGroupOptions.get(groupAId)?.name;
            const groupBName = driverGroupOptions.get(groupBId)?.name;
            if (groupAName && groupBName) {
              return groupAName.localeCompare(groupBName);
            }
            return 0;
          }
        },
        cellRenderer: (params: ICellRendererParams<number>) => {
          const group = driverGroupOptions.get(params.value)?.name;
          return group ?? EmptyValueStrings.notProvided;
        }
      },
      {
        field: "vehicleTypeId",
        headerName: "Vehicle",
        sortable: false,
        type: "filterColumn",
        filter: "agSetColumnFilter",
        filterParams: {
          values: (vehicleTypesResults.data?.searchVehicleTypes?.items ?? [])
            .filter(Boolean)
            .map((v) => v?.vehicleTypeId),
          valueFormatter: (params: ValueFormatterParams<number>) => {
            const vehicleDescription = (vehicleTypesResults.data?.searchVehicleTypes?.items ?? []).find(
              (item) => item?.vehicleTypeId === params.value
            )?.description;
            return vehicleDescription ?? EmptyValueStrings.notProvided;
          }
        },
        cellRenderer: (params: ICellRendererParams<number>) => {
          const vehicleDescription = (vehicleTypesResults.data?.searchVehicleTypes?.items ?? []).find(
            (item) => item?.vehicleTypeId === params.value
          )?.description;
          return (
            <FontAwesomeIcon
              data-testid={"vehicle-icon"}
              size={"lg"}
              color={"#797979"}
              icon={VehicleIconResolver(vehicleDescription, tenantPreferences)}
              title={vehicleDescription as string}
              style={{margin: "auto"}}
            />
          );
        }
      },
      ...(skillsEnabled
        ? [
            {
              field: "skills",
              headerName: "Skills",
              sortable: false,
              valueGetter: (params: any) => {
                const skillsStr = params.data?.skills as string | undefined;
                if (!skillsStr) return "";
                return skillsStr
                  .split(",")
                  .map((s: string) => s.trim())
                  .filter(Boolean)
                  .map((id: string) => allSkills.find((s) => s.id === Number(id))?.name)
                  .filter(Boolean)
                  .join(", ");
              },
              flex: 2
            } as ColDef<Driver>
          ]
        : [])
    ];
  }, [
    driverGroupOptions,
    tenantPreferences,
    vehicleTypesResults.data?.searchVehicleTypes?.items,
    skillsEnabled,
    allSkills
  ]);

  // Initialize the grid and apply saved preferences
  useEffect(() => {
    if (gridReady) {
      setDatasource(
        createDriverServerSideDatasource({
          client: apolloClient,
          tokenExpiry: authState.expiresAt ? new Date(authState.expiresAt) : undefined
        })
      );
      if (initializing && isUserPreferencesLoaded) {
        const savedDriverGroupFilterValues = extractJsonPref(
          userPreferences,
          UserPreferences.manifestGroupHeader
        ).value;
        if (isArray(savedDriverGroupFilterValues)) {
          // Ensure the saved values are valid in driverGroupOptions
          const validGroupIds = new Set(Array.from(driverGroupOptions.values()).map((item) => item.id));
          const filteredValues = savedDriverGroupFilterValues.filter((id) => validGroupIds.has(id));
          setSelectedDriverGroupIds(filteredValues);
        }
        setInitializing(false);
      }
    }
  }, [
    apolloClient,
    authState.expiresAt,
    driverGroupOptions,
    getFilterInstance,
    gridReady,
    initializing,
    isUserPreferencesLoaded,
    refreshDataByFilterChanged,
    selectedDriverGroupIds,
    userPreferences
  ]);

  //if no drivers groups are selected, but has selected dispatch stations, set the global filter to only include drivers  of those dispatch stations
  useEffect(() => {
    setGridContext({
      ...gridContext,
      globalFilter: hasSelectedDispatchStations(dispatchStationState)
        ? {
            or: Array.from(driverGroupOptions.values()).map((dg) => {
              return {dispatchGroupId: {eq: dg.id}} as ModelDriverFilterInput;
            })
          }
        : undefined
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchStationState, driverGroupOptions]);

  // Set filter values for dispatchGroup.dispatchGroupId when driverGroupOptions changes
  useEffect(() => {
    if (gridReady && driverGroupOptions.size > 0) {
      getFilterInstance("dispatchGroup.dispatchGroupId").then((filter) => {
        const values = Array.from(driverGroupOptions.values()).map((item) => item.id.toString());
        filter?.setFilterValues(values);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverGroupOptions, gridReady, getFilterInstance]);

  // Reset the selected driver group ids when selectedDispatchStations changes
  useEffect(
    () => {
      setSelectedDriverGroupIds([]);
      syncModelFilter("dispatchGroup.dispatchGroupId", null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDispatchStations.size]
  );

  // Refresh data when driverGroupOptions changes
  useEffect(() => {
    if (gridReady) {
      refreshDataByFilterChanged("driverGroupOptions");
    }
  }, [driverGroupOptions, gridReady, refreshDataByFilterChanged]);

  // Refresh data and Update the drivers group filter model when selectedJobGroups changed
  useEffect(() => {
    if (gridReady && !initializing) {
      syncModelFilter(
        "dispatchGroupId",
        selectedDriverGroupIds.length > 0
          ? {type: "set", values: selectedDriverGroupIds.map((item) => item.toString())}
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverGroupIds]);

  // Refresh data when gridContext changed
  useEffect(() => {
    if (gridReady) {
      refreshDataByFilterChanged("gridContext");
    }
  }, [gridContext, gridReady, refreshDataByFilterChanged]);

  // Clear selected rows when the state was reset
  useEffect(() => {
    if (gridReady && newDrivers.length === 0) {
      gridRef.current?.api.deselectAll();
    }
  }, [gridReady, newDrivers.length]);

  return (
    <PanelContainer className="ag-theme-alpine">
      <ButtonBar hasWarningMessage={false}>
        <ButtonContainer>
          <LeftControls>
            <MultiSelectDropDown
              testIdPrefix={"driver-group-multiselect"}
              text={"Groups"}
              items={driverGroupOptions}
              onSelected={handleDriverGroupSelected}
              onUnselected={handleDriverGroupUnselected}
              onUnselectedAll={handleDriverGroupUnselectedAll}
              clearFilter
            />
          </LeftControls>
          <RightControls>
            <ControlGroup>
              <Search
                idPrefix={"drivers"}
                onSearch={handleGlobalSearch}
                isConstrainedByFilters={gridHasActiveFilters}
                minimal
              />
            </ControlGroup>
          </RightControls>
        </ButtonContainer>
      </ButtonBar>
      <AgGridReact<Driver>
        suppressAggAtRootLevel
        className={"drivers-grid"}
        ref={gridRef}
        onGridReady={handleGridReady}
        getRowId={getRowId}
        context={gridContext}
        suppressCellFocus
        columnDefs={columnDefs}
        defaultColDef={DefaultColumnDef}
        columnTypes={ColumnTypes}
        rowSelection={"multiple"}
        rowMultiSelectWithClick
        onSelectionChanged={handleSelectionChanged}
        onFilterChanged={handleFilterChanged}
        enableCellTextSelection
        ensureDomOrder
        rowModelType={"serverSide"}
        serverSideDatasource={datasource}
        pagination
        paginationPageSize={150}
        cacheBlockSize={150}
        animateRows
        maintainColumnOrder
        rowStyle={CustomRowStyle}
        suppressDragLeaveHidesColumns
        suppressMultiSort={!multipleSortEnabled}
      />
      <SelectedInfo>
        <Text>{newDrivers.length} selected</Text>
        {newDrivers.length > 0 && (
          <DangerButton
            data-testid="clear-selected-drivers-button"
            variant="minimal"
            intent="danger"
            onClick={handleClearAllSelectedDrivers}
          >
            Clear
          </DangerButton>
        )}
      </SelectedInfo>
    </PanelContainer>
  );
};

export default DriversGrid;
